<template>
  <ul ref="list" class="list" role="listbox" aria-labelledby="list-component">
    <template v-if="hasResults">
      <li
        v-for="(option, i) in displayOptions"
        :key="option.value"
        :ref="`option-${i}`"
        role="option"
        :tabindex="option.type === 'group' ? -1 : 0"
        :aria-selected="option.value === value"
        :class="optionClasses[i]"
        :title="option.name"
        @click="onSelect(option.value)"
        @keydown.enter="onSelect(option.value)"
        v-html="option.name"
      />
    </template>
  </ul>
</template>

<script>
export default {
  name: 'List',

  props: {
    autocomplete: Boolean,

    maxResults: {
      type: Number,
      default: undefined,
    },

    options: {
      type: [Array, Object],
      required: true,
    },

    value: {
      type: [String, Number],
      default: '',
    },
  },

  computed: {
    currentValue() {
      const val = this.options.find(({ value }) => value === this.value)
      return val ? val.name : this.value
    },

    displayOptions() {
      if (this.autocomplete && this.value) {
        const options = this.options
          .filter(option =>
            option.name.toLowerCase().includes(this.value.toLowerCase()),
          )
          .map(({ name, value }) => {
            const reg = new RegExp(this.value, 'gi')
            return {
              name: name.replace(reg, match => `<b>${match}</b>`),
              value,
            }
          })

        return this.maxResults ? options.slice(0, this.maxResults) : options
      } else if (this.hasGroups) {
        return this.groupOptions(this.options)
      }

      return this.options
    },

    hasGroups() {
      return !Array.isArray(this.options)
    },

    hasResults() {
      return this.displayOptions.length !== 0
    },

    optionClasses() {
      return this.displayOptions.map(option => [
        'item',
        {
          group: option.type === 'group',
          groupOption: this.hasGroups && option.type !== 'group',
          selected: option.value === this.value,
        },
      ])
    },

    optionRefs() {
      return Object.entries(this.$refs)
        .filter(([name, el]) => el.length)
        .filter(
          ([name, el]) =>
            name.startsWith('option-') && !el[0].classList.contains('group'),
        )
    },
  },

  methods: {
    focusNext() {
      if (!this.hasResults) {
        return
      }

      const currentIndex = this.optionRefs.findIndex(
        option => option[0] === `option-${this.getFocusedItem()}`,
      )
      const nextRef = this.optionRefs[currentIndex + 1] || this.optionRefs[0]
      const nextElement = nextRef[1][0]

      nextElement.focus()
    },

    focusPrevious() {
      if (!this.hasResults) {
        return
      }

      const currentIndex = this.optionRefs.findIndex(
        option => option[0] === `option-${this.getFocusedItem()}`,
      )
      const previousRef =
        this.optionRefs[currentIndex - 1] ||
        this.optionRefs[this.optionRefs.length - 1]
      const previousElement = previousRef[1][0]

      previousElement.focus()
    },

    focusToLetter(letter) {
      const index = this.displayOptions.findIndex(
        option =>
          option.type !== 'group' && option.name[0].toLowerCase() === letter,
      )

      if (index !== -1) {
        this.$refs[`option-${index}`][0].focus()
      }
    },

    getBoundingClientRect(value) {
      return this.$refs.list.getBoundingClientRect()
    },

    getFocusedItem() {
      return this.displayOptions.findIndex(
        option =>
          option.name.replace(/<\/?b>/g, '') ===
          document.activeElement.textContent,
      )
    },

    groupOptions(options) {
      return Object.entries(options).reduce(
        (acc, [group, groupOptions]) => [
          ...acc,
          { name: group, type: 'group' },
          ...groupOptions,
        ],
        [],
      )
    },

    onSelect(value) {
      this.$emit('input', value)
    },

    useCurrent() {
      this.$emit('input', this.value)
    },
  },
}
</script>

<style lang="postcss" scoped>
.list {
  display: flex;
  flex-direction: column;
  list-style: none;
  z-index: 99;
  max-height: 320px;
  overflow: auto;
  min-width: 60px;
  padding: 0;
  box-shadow: 0 2px 4px 0 var(--c-primary-neutral-1);
  -ms-overflow-style: none; /* Edge */
  scrollbar-width: none; /* Firefox */
  overflow-x: hidden;
}

.item {
  height: 40px;
  min-height: 40px;
  background-color: var(--c-primary-neutral-3);
  cursor: pointer;
  position: relative;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 20px;
  font-weight: normal;
  font-style: normal;
  line-height: 2;
  color: var(--c-primary-neutral-1);
  padding: 0 10px;

  &:not(:last-child) {
    border-bottom: 1px solid var(--c-secondary-neutral-3);
  }

  &.group {
    cursor: default;
  }

  &.selected,
  &.group {
    background-color: var(--c-secondary-neutral-3);
  }

  &.groupOption {
    border-bottom: none;
    padding-left: 20px;
  }

  &:hover:not(.group),
  &:focus {
    background-color: var(--c-secondary-neutral-3);
  }

  &:focus {
    z-index: 1;
  }
}

.reset {
  background-color: transparent;
  border: none;
  align-self: flex-end;
  color: var(--c-primary-color-3);
  font-size: 15px;
  margin-top: 10px;
  font-weight: bold;
}
</style>
